import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr } from "../../api/client";
import { ADMIN } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { ArrowLeft, MapPin, DollarSign, Clock, Truck, Edit3, RotateCcw, Flag, FileText } from "lucide-react";
import { toast } from "sonner";

function money(n) {
    return `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function JobAudit() {
    const { id } = useParams();
    const nav = useNavigate();
    const [audit, setAudit] = useState(null);
    const [overrideOn, setOverrideOn] = useState(null); // entry row
    const [oAmount, setOAmount] = useState("");
    const [oReason, setOReason] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/v1/admin/jobs/${id}/audit`);
            setAudit(data);
        } catch (e) { toast.error(apiErr(e)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    if (!audit) return <div className="p-8 text-muted">Loading audit…</div>;

    const { job, totals, activity, contractor_costs, variations } = audit;
    // Margin gradient
    const marginPositive = totals.margin >= 0;
    const burnPct = Math.min(100, Math.max(0, totals.burn_pct || 0));

    const openOverride = (row) => {
        setOverrideOn(row);
        setOAmount(String(row.rate));
        setOReason("");
    };
    const applyOverride = async () => {
        if (!oReason.trim()) { toast.error("Reason is required"); return; }
        setBusy(true);
        try {
            await api.post(`/v1/admin/time-entries/${overrideOn.entry_id}/rate-override`, {
                amount: parseFloat(oAmount) || 0,
                reason: oReason,
            });
            toast.success("Rate override applied · original preserved in audit log");
            setOverrideOn(null);
            await load();
        } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    };
    const clearOverride = async (row) => {
        try {
            await api.delete(`/v1/admin/time-entries/${row.entry_id}/rate-override`);
            toast.success("Override cleared");
            await load();
        } catch (e) { toast.error(apiErr(e)); }
    };

    return (
        <div data-testid={ADMIN.auditRoot} className="space-y-6 max-w-6xl">
            <button
                onClick={() => nav(`/admin/jobs/${id}`)}
                className="text-muted hover:text-ink flex items-center gap-1.5 text-sm"
            >
                <ArrowLeft size={16} /> Job detail
            </button>

            <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/reports/job-audit/${id}.pdf`}
                target="_blank" rel="noreferrer"
                data-testid="audit-pdf-link"
                className="km-chip km-chip-navy inline-flex items-center gap-1 self-start"
            >
                <FileText size={12} /> Export P&amp;L PDF
            </a>

            {/* Job header */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex items-center gap-5">
                    <div className="w-14 h-14 rounded-md bg-[color:var(--km-accent)]/10 flex items-center justify-center">
                        <MapPin size={22} className="text-accent" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="eyebrow">P&amp;L Audit</div>
                            <div className="num text-sm text-ink">{job.job_number || "unnumbered"}</div>
                        </div>
                        <h1 className="text-3xl font-extrabold tracking-[-0.03em] leading-none mt-1">{job.address}</h1>
                        <div className="text-muted text-sm mt-1">{job.description || "—"}</div>
                    </div>
                </div>
                <StatusBadge status={job.status} />
            </div>

            {/* Totals cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <TotalCard label="Quoted" value={money(totals.quoted)} hint={`+ ${money(totals.variations_approved)} variations`} accent="var(--km-navy)" />
                <TotalCard label="Revenue" value={money(totals.revenue)} hint="quoted + approved variations" accent="var(--km-navy-d)" />
                <TotalCard label="Actual cost" value={money(totals.actual_cost)} hint={`labour ${money(totals.labor_cost)} · sub ${money(totals.contractor_cost)}`} accent="var(--km-red)" />
                <TotalCard
                    label="Margin"
                    value={money(totals.margin)}
                    hint={`${totals.margin_pct.toFixed(1)}%`}
                    accent={marginPositive ? "var(--km-green)" : "var(--km-red)"}
                    valueColor={marginPositive ? "var(--km-green-d)" : "var(--km-red)"}
                />
            </div>

            {/* Progress bar — burn vs revenue */}
            <div className="km-card p-6" data-testid={ADMIN.auditBar}>
                <div className="flex items-baseline justify-between mb-3">
                    <div className="eyebrow">Cost burned vs revenue</div>
                    <div className="num text-sm">
                        <span className="font-bold">{money(totals.actual_cost)}</span>
                        <span className="text-muted"> / {money(totals.revenue)}</span>
                        <span className="text-muted ml-2">({burnPct.toFixed(1)}%)</span>
                    </div>
                </div>
                <div className="h-4 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                            width: `${burnPct}%`,
                            background: burnPct >= 100
                                ? "linear-gradient(90deg, var(--km-red) 0%, var(--km-red-d) 100%)"
                                : burnPct >= 80
                                    ? "linear-gradient(90deg, var(--km-yellow) 0%, var(--km-red) 100%)"
                                    : "linear-gradient(90deg, var(--km-green) 0%, var(--km-navy) 100%)",
                        }}
                    />
                </div>
                <div className="grid grid-cols-3 gap-4 mt-4 text-xs">
                    <MiniStat icon={Clock} label="Hours" value={`${totals.labor_hours.toFixed(1)}h`} />
                    <MiniStat icon={Truck} label="Kilometres" value={`${totals.km.toFixed(0)} km`} />
                    <MiniStat icon={DollarSign} label="Contractor" value={money(totals.contractor_cost)} />
                </div>
            </div>

            {/* Activity table */}
            <div className="km-card overflow-hidden" data-testid={ADMIN.auditActivity}>
                <div className="px-5 py-4 border-b border-km flex items-center justify-between">
                    <h2 className="text-lg font-extrabold tracking-[-0.03em]">Time entries · {activity.length}</h2>
                    <div className="text-xs text-muted">Ken can override rates below · reason required</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[11px] uppercase tracking-[0.1em] text-muted border-b border-km">
                                <th className="px-4 py-3 font-medium">Date</th>
                                <th className="px-4 py-3 font-medium">Who</th>
                                <th className="px-4 py-3 font-medium">Type</th>
                                <th className="px-4 py-3 font-medium text-right">Hours</th>
                                <th className="px-4 py-3 font-medium text-right">Rate</th>
                                <th className="px-4 py-3 font-medium text-right">Cost</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {activity.map((r) => {
                                const overridden = !!r.rate_override;
                                return (
                                    <tr key={r.entry_id} data-testid={ADMIN.auditActivityRow(r.entry_id)} className="border-b border-km hover:bg-[color:var(--surface-2)]">
                                        <td className="px-4 py-3 num text-xs">{r.date}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <InitialsAvatar name={r.user?.name} initials={r.user?.initials} size={26} />
                                                <span className="text-sm">{r.user?.name || "—"}</span>
                                                {r.is_variation && (
                                                    <span className="km-chip km-chip-red inline-flex items-center gap-1">
                                                        <Flag size={9} /> Var
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="km-chip km-chip-grey">{r.work_type}</span>
                                        </td>
                                        <td className="px-4 py-3 num text-right">{r.hours.toFixed(2)}</td>
                                        <td className="px-4 py-3 num text-right">
                                            {overridden ? (
                                                <span className="inline-flex items-center gap-1">
                                                    <span className="text-muted line-through text-xs">{money(r.original_rate)}</span>
                                                    <span className="font-bold text-[color:var(--km-yellow-d)]">{money(r.rate)}</span>
                                                </span>
                                            ) : (
                                                <span>{money(r.rate)}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 num text-right font-medium">{money(r.cost)}</td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button
                                                data-testid={ADMIN.rateOverrideBtn(r.entry_id)}
                                                onClick={() => openOverride(r)}
                                                className="text-xs text-[color:var(--km-navy)] hover:underline inline-flex items-center gap-1"
                                            >
                                                <Edit3 size={11} /> Override
                                            </button>
                                            {overridden && (
                                                <button
                                                    onClick={() => clearOverride(r)}
                                                    className="text-xs text-muted hover:text-ink inline-flex items-center gap-1 ml-3"
                                                >
                                                    <RotateCcw size={11} /> Reset
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {activity.length === 0 && (
                                <tr><td colSpan={7} className="py-8 text-center text-muted text-sm">No time entries logged yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Contractor costs + variations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="km-card p-5">
                    <h3 className="text-base font-extrabold tracking-[-0.03em] mb-3">Contractor costs · {money(totals.contractor_cost)}</h3>
                    <ul className="divide-y divide-km">
                        {contractor_costs.map((c) => (
                            <li key={c.id} className="py-2 flex items-center justify-between text-sm">
                                <span>{c.label}</span>
                                <span className="num font-medium">{money(c.amount)}</span>
                            </li>
                        ))}
                        {contractor_costs.length === 0 && <li className="py-4 text-center text-muted text-sm">None recorded.</li>}
                    </ul>
                </div>
                <div className="km-card p-5">
                    <h3 className="text-base font-extrabold tracking-[-0.03em] mb-3">Variations</h3>
                    <div className="text-xs text-muted mb-2">Approved · {money(totals.variations_approved)}</div>
                    <ul className="divide-y divide-km">
                        {variations.approved.map((v) => (
                            <li key={v.id} className="py-2 flex items-center justify-between text-sm">
                                <span className="truncate mr-3">{v.description}</span>
                                <span className="num font-medium text-[color:var(--km-green)]">{money(v.amount)}</span>
                            </li>
                        ))}
                        {variations.pending.length > 0 && (
                            <li className="pt-3 pb-1 text-[11px] uppercase tracking-wider text-muted">Pending</li>
                        )}
                        {variations.pending.map((v) => (
                            <li key={v.id} className="py-2 flex items-center justify-between text-sm">
                                <span className="truncate mr-3 text-muted">{v.description}</span>
                                <span className="num text-muted">{money(v.amount)}</span>
                            </li>
                        ))}
                        {variations.approved.length === 0 && variations.pending.length === 0 && (
                            <li className="py-4 text-center text-muted text-sm">No variations.</li>
                        )}
                    </ul>
                </div>
            </div>

            {/* Rate override dialog */}
            <Dialog open={!!overrideOn} onOpenChange={(o) => !o && setOverrideOn(null)}>
                <DialogContent data-testid={ADMIN.rateOverrideDialog}>
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold tracking-[-0.03em]">Rate override</DialogTitle>
                    </DialogHeader>
                    {overrideOn && (
                        <div className="space-y-4">
                            <div className="text-sm">
                                <div className="text-ink font-medium">{overrideOn.user?.name} · {overrideOn.date}</div>
                                <div className="text-xs text-muted">Original rate: <span className="num">{money(overrideOn.original_rate)}</span></div>
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.12em] text-muted">New rate ($/hr)</label>
                                <Input
                                    data-testid={ADMIN.rateOverrideAmount}
                                    type="number" step="0.01" min="0" className="num"
                                    value={oAmount} onChange={(e) => setOAmount(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.12em] text-muted">Reason (required)</label>
                                <Textarea
                                    data-testid={ADMIN.rateOverrideReason}
                                    rows={2} value={oReason} onChange={(e) => setOReason(e.target.value)}
                                    placeholder="Why is this different from the resolved rate?"
                                />
                            </div>
                            <div className="text-xs text-muted">
                                Both the original and override will be recorded on the entry &amp; in the audit log.
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOverrideOn(null)}>Cancel</Button>
                        <Button
                            data-testid={ADMIN.rateOverrideSubmit}
                            onClick={applyOverride}
                            disabled={!oReason.trim() || busy}
                        >
                            Apply override
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function TotalCard({ label, value, hint, accent, valueColor }) {
    return (
        <div className="km-card px-5 py-4 relative overflow-hidden">
            <div className="eyebrow">{label}</div>
            <div className="num text-2xl font-extrabold tracking-[-0.03em] mt-1" style={valueColor ? { color: valueColor } : undefined}>{value}</div>
            {hint && <div className="text-xs text-muted mt-1">{hint}</div>}
            {accent && (
                <div className="absolute right-0 bottom-0 w-14 h-14 rounded-full opacity-[0.06]" style={{ background: accent, transform: "translate(30%, 30%)" }} />
            )}
        </div>
    );
}

function MiniStat({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-2 text-ink">
            <Icon size={14} className="text-muted" />
            <span className="text-muted">{label}</span>
            <span className="num font-medium ml-auto">{value}</span>
        </div>
    );
}
